import { useState, useEffect, useContext, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminHeader from './components/AdminHeader';
import Footer from './components/Footer';
import AdminSidebar from './components/AdminSidebar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard/admin/Dashboard';
import Users from './pages/Dashboard/admin/Users';
import UserManage from './pages/Dashboard/admin/UserManage';
import Home from './pages/Public/Home';
import PendingVerifications from './pages/Dashboard/admin/PendingVerification';
import Diseases from './pages/Dashboard/admin/Diseases';
import Consultations from './pages/Dashboard/admin/Consultations';
import Waste from './pages/Dashboard/admin/Waste';
import Bids from './pages/Dashboard/admin/Bids';
import AdminBidPage from './pages/Dashboard/admin/adminBidPage';
import Content from './pages/Dashboard/admin/Content';
import Categories from './pages/Dashboard/admin/Categories';
import Analytics from './pages/Dashboard/admin/Analytics';
import UsageReports from './pages/Dashboard/admin/UsageReports';
import FeedbackReview from './pages/Dashboard/admin/FeedbackReview';
import AiCapabilitiesRegistry from './pages/Dashboard/admin/AiCapabilitiesRegistry';
import ProductionRecords from './pages/Dashboard/admin/ProductionRecords';
import TrialDeployments from './pages/Dashboard/admin/TrialDeployments';
import { auth } from './utils/auth';
import VerifyEmail from './pages/verify-email';
import PublicLayout from './components/DashboardLayouts/PublicLayout';
import UserDashboardLayout from './components/DashboardLayouts/UserDashboardLayout';
import Header from './pages/Public/Header';
import About from './pages/Public/About';
import EducationalContentList from './pages/EducationalContentList';
import EducationalContentDetails from './pages/EducationalContentDetails';
import ContactUs from './pages/Public/ContactUs';
import ServicesPage from './pages/Public/ServicesPage';
import { AuthContext, AuthProvider } from './context/AuthContext';
import DoctorBookingPage from './pages/DoctorBookingPage';
import Specialits from './pages/Specialists';
import WasteRequests from './pages/WasteRequests';
import WasteRequestDetails from './pages/WasteRequestDetails';
import ChatbotPage from './pages/ChatbotPage'
import SubscriptionManage from './pages/Dashboard/admin/SubscriptionManage';
import SubscriptionBundle from './pages/SubscriptionBundle';
import UserDashboard from './pages/Dashboard/userDashboard/UserDashboard';
import UserConsultation from './pages/Dashboard/userDashboard/UserConsultations';
import UserProfile from './pages/Dashboard/userDashboard/UserProfile';
import UserSubscriptions from './pages/Dashboard/userDashboard/UserSubscriptions';
import SpecialistConsultations from './pages/Dashboard/specialist/SpecialistConsultations';
import FarmerWasteManagement from './pages/Dashboard/farmerDashbaord/FarmerWasteManagement';
import BidListPage from './pages/Dashboard/farmerDashbaord/BidListPage';
import MyWasteBids from './pages/Dashboard/wasterCollectorDashboard/MyWasteBids';
import Chat from './pages/Dashboard/userDashboard/Chat';
import FarmerWasteCollectorChat from './pages/FarmerWasteCollectorChat';
import AdminSubscriptions from './pages/Dashboard/admin/AdminSubscriptions';
import UserSubscriptionList from './pages/Dashboard/admin/UserSubscriptionList';
import PrivacyPolicyManagement from './pages/Dashboard/admin/PrivacyPolicyManagement';
import PrivacyPolicyPage from './pages/Public/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/Public/TermsAndConditionsPage';
import TermsAndConditions from './pages/Dashboard/admin/TermsAndConditions';
import AboutUs from './pages/Dashboard/admin/AboutUs';
import SettingsPage from './pages/Dashboard/admin/SettingsPage.jsx';
import WasteAnalytics from './pages/Dashboard/admin/WasteAnalytics.jsx';
import AdminWasteAnalytics from './pages/Dashboard/admin/AdminWasteAnalytics.jsx';
import PaymentSuccess from './pages/PaymentSuccess.jsx';
import RealTimeMonitoring from './pages/Dashboard/monitoring/RealTimeMonitoring';
import AiActivityHistory from './pages/Dashboard/monitoring/AiActivityHistory';
import AiDiseaseDiagnosis from './pages/Dashboard/monitoring/AiDiseaseDiagnosis';
import AiEnvironmentalAnalysis from './pages/Dashboard/monitoring/AiEnvironmentalAnalysis';
import AiProductivityForecast from './pages/Dashboard/monitoring/AiProductivityForecast';
import ResearchArchive from './pages/Dashboard/admin/ResearchArchive';
import ProductivityForecast from './pages/Dashboard/admin/ProductivityForecast';
import AdminMonitoring from './pages/Dashboard/monitoring/AdminMonitoring';
import FarmerAnalytics from './pages/Dashboard/analytics/FarmerAnalytics';
import SubscriptionAnalytics from './pages/Dashboard/analytics/SubscriptionAnalytics';

const ProtectedLayout = ({ children, isSidebarOpen, toggleSidebar, requiredRole }) => {
  const { user, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (!user) {
    toast.error('Please log in to access this page', {
      position: 'top-right',
      autoClose: 5000,
    });
    return <Navigate to="/login" />;
  }
  if (requiredRole && !auth.hasRole(requiredRole)) {
    toast.error(`Access denied: ${requiredRole} only`, {
      position: 'top-right',
      autoClose: 5000,
    });
    return <Navigate to="/login" />;
  }

  const isAdminRoute = requiredRole === 'admin';
  const HeaderComponent = isAdminRoute ? AdminHeader : Header;

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderComponent toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
      <div className="flex flex-1">
        {isAdminRoute && <AdminSidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />}
        <main
          className={`flex-1 p-4 lg:p-5 transition-all duration-300 mx-auto w-full max-w-[100%] sm:max-w-full ${
            isAdminRoute && isSidebarOpen ? 'lg:ml-64' : isAdminRoute ? 'lg:ml-16' : ''
          }`}
          onTransitionEnd={() => console.log('Main content margin:', isAdminRoute && isSidebarOpen ? 'ml-64' : isAdminRoute ? 'ml-16' : '0')}
        >
          {children}
        </main>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return window.innerWidth >= 1024;
  });

  const toggleSidebar = () => {
    console.log('Toggling sidebar, current state:', isSidebarOpen);
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const ProtectedChatbotRoute = () => {
    const { user, isLoading } = useContext(AuthContext);

    if (isLoading) {
      return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    if (!user) {
      toast.error('Please sign in to use the chatbot feature', {
        position: 'top-right',
        autoClose: 5000,
      });
      return <Navigate to="/login" />;
    }

    return <ChatbotPage />;
  };
  console.log('Rendering ToastContainer in App.jsx');

  return (
    <AuthProvider> {/* AuthProvider wraps the app */}
      <BrowserRouter>
        <div className="min-h-screen dark:bg-gray-800">
          <Suspense fallback={<div className="flex justify-center items-center h-screen">Loading...</div>}>
            <Routes>
              <Route path="/" element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="subscriptions" element={<SubscriptionBundle />} />
                <Route path="services" element={<ServicesPage />} />
                <Route path="contact" element={<ContactUs />} />
                <Route path="book/:id" element={<DoctorBookingPage />} />
                <Route path="login" element={<Login />} />
                 <Route path="Signup" element={<Signup />} />
                <Route path="home" element={<Home />} />
                <Route path="specialists" element={<Specialits />} />
                <Route path="WasteRequests" element={<WasteRequests />} />
                <Route path="waste-requests/:id" element={<WasteRequestDetails />} />
                <Route path="farming-insights" element={<EducationalContentList />} />
                <Route path="farming-insights/:id" element={<EducationalContentDetails />} />
                {/* <Route path="chatbot" element={<ChatbotPage />} /> */}
                <Route path="chatbot" element={<ProtectedChatbotRoute />} />
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="Terms-and-conditions" element={<TermsAndConditionsPage />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
              </Route>
              

               <Route path="/user" element={<UserDashboardLayout />}>
                <Route index element={<UserDashboard />}  equiredRole="user"/>
                 <Route path="consultations" element={<UserConsultation />} />
                 <Route path="profile" element={<UserProfile />} />
                 <Route path="subscription" element={<UserSubscriptions />} />
                 <Route path="my-bookings" element={<SpecialistConsultations />} />
                 <Route path="chat/:consultationId" element={<Chat />} />
                 <Route path="analytics" element={<FarmerAnalytics />} />
                 <Route path="subscription-analytics" element={<SubscriptionAnalytics />} />
                 <Route path="monitoring" element={<RealTimeMonitoring />} />
                 <Route path="ai-history" element={<AiActivityHistory />} />
                 <Route path="ai-diagnosis" element={<AiDiseaseDiagnosis />} />
                 <Route path="environmental-analysis" element={<AiEnvironmentalAnalysis />} />
                 <Route path="productivity-forecast" element={<AiProductivityForecast />} />

              </Route>
               <Route path="/waste-collector" element={<UserDashboardLayout />}>
                <Route index element={<UserDashboard />}  equiredRole="user"/>
                 <Route path="profile" element={<UserProfile />} />
                 <Route path="subscription" element={<UserSubscriptions />} />
                 <Route path="my-bids" element={<MyWasteBids />} />
                 <Route path="chat/:consultationId" element={<Chat />} />
                 <Route path="chat/farmer-waste-collector/:consultationId" element={<FarmerWasteCollectorChat />} />
                 <Route path="waste-analytics" element={<WasteAnalytics />} />
                 <Route path="subscription-analytics" element={<SubscriptionAnalytics />} />
                 <Route path="monitoring" element={<RealTimeMonitoring />} />
                 <Route path="ai-history" element={<AiActivityHistory />} />
                 <Route path="ai-diagnosis" element={<AiDiseaseDiagnosis />} />
                 <Route path="environmental-analysis" element={<AiEnvironmentalAnalysis />} />
                 <Route path="productivity-forecast" element={<AiProductivityForecast />} />
              </Route>
                <Route path="/farmer" element={<UserDashboardLayout />}>
                 <Route index element={<UserDashboard />}  equiredRole="farmer"/>
                 <Route path="bids/:requestId" element={<BidListPage />} />
                 <Route path="waste-management" element={<FarmerWasteManagement />} />
                 <Route path="consultations" element={<UserConsultation />} />
                 <Route path="profile" element={<UserProfile />} />
                 <Route path="subscription" element={<UserSubscriptions />} />
                 <Route path="my-bookings" element={<SpecialistConsultations />} />
                 <Route path="chat/:consultationId" element={<Chat />} />
                 <Route path="chat/farmer-waste-collector/:consultationId" element={<FarmerWasteCollectorChat />} />
                 <Route path="monitoring" element={<RealTimeMonitoring />} />
                 <Route path="ai-history" element={<AiActivityHistory />} />
                 <Route path="ai-diagnosis" element={<AiDiseaseDiagnosis />} />
                 <Route path="environmental-analysis" element={<AiEnvironmentalAnalysis />} />
                 <Route path="productivity-forecast" element={<AiProductivityForecast />} />
                 <Route path="analytics" element={<FarmerAnalytics />} />
                 <Route path="waste-analytics" element={<WasteAnalytics />} />
                 <Route path="subscription-analytics" element={<SubscriptionAnalytics />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Dashboard />
                  </ProtectedLayout>
                }
              />
            
              <Route
                path="/admin/monitoring"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <AdminMonitoring />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/bids/:requestId"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <AdminBidPage />
                  </ProtectedLayout>
                }
              />
                <Route
                path="/admin/privacy-policy"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <PrivacyPolicyManagement/>
                  </ProtectedLayout>
                }
              />
                 <Route
                path="/admin/term-and-conditions"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <TermsAndConditions/>
                  </ProtectedLayout>
                }
              />
                <Route
                path="/admin/about-us"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <AboutUs/>
                  </ProtectedLayout>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Users />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/users/:id"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <UserManage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/users/pending-verifications"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <PendingVerifications />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/diseases"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Diseases />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/consultations"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Consultations />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/waste"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Waste />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/bids"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Bids />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/manage-subscription-plans"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <SubscriptionManage />
                  </ProtectedLayout>
                }
              />
                  <Route
                path="/manage-users-subscriptions"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <AdminSubscriptions />
                  </ProtectedLayout>
                }
              />
                  <Route
                path="/user-subscriptions/:userId"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <UserSubscriptionList />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/content"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Content />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/categories"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Categories />
                  </ProtectedLayout>
                }
              />
             
               <Route
                path="/admin/waste-analytics"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <AdminWasteAnalytics />
                  </ProtectedLayout>
                }
              />
              
              <Route
                path="/analytics"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <Analytics />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/usage-reports"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <UsageReports />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/feedback"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <FeedbackReview />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/ai-capabilities"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <AiCapabilitiesRegistry />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/production-records"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <ProductionRecords />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/trial-deployments"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <TrialDeployments />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/research-archive"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <ResearchArchive />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/productivity-forecast"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <ProductivityForecast />
                  </ProtectedLayout>
                }
              />
                <Route
                path="/settings"
                element={
                  <ProtectedLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} requiredRole="admin">
                    <SettingsPage />
                  </ProtectedLayout>
                }
              />
              <Route path="*" element={<Navigate to="/" />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Routes>
          </Suspense>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            limit={1}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;